import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Tag,
  Select,
  Typography,
  Tooltip,
  Tabs,
  Card,
  Row,
  Col,
  Statistic,
  Badge,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  UndoOutlined,
  DollarOutlined,
  RollbackOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  LockOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { hasPermission } from '../../utils/permissions';
import {
  fetchReturnOrdersRequest,
  fetchCreditNotesRequest,
  approveReturnOrderRequest,
} from '../../redux/actions/returnOrderActions';
import ReturnOrderForm from './ReturnOrderForm';
import ReturnOrderDetail, { getReturnStatusTag } from './ReturnOrderDetail';
import CreditNoteList, { getCreditNoteStatusTag } from './CreditNoteList';
import { fetchOrdersRequest } from '../../containers/PurchaseOrder/actions';

const { Text } = Typography;
const { Option } = Select;

const fmtMoney = (v) => (v != null ? `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—');
const fmtDate  = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');

const ReturnOrderList = () => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { returnOrders, creditNotes, loading, actionLoading } = useSelector((s) => s.returns);

  const canView    = hasPermission(permissions, 'delivery_challan/view');
  const canCreate  = hasPermission(permissions, 'delivery_challan/create');
  const canUpdate  = hasPermission(permissions, 'delivery_challan/update');

  const [activeTab, setActiveTab]   = useState('returns');
  const [formOpen, setFormOpen]     = useState(false);
  const [detailId, setDetailId]     = useState(null);
  const [statusFilter, setStatus]   = useState('');

  const loadReturns = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    dispatch(fetchReturnOrdersRequest(params));
  }, [dispatch, statusFilter]);

  useEffect(() => {
    if (canView) {
      dispatch(fetchOrdersRequest(1, 100)); // load orders for form dropdown
      loadReturns();
      dispatch(fetchCreditNotesRequest({}));
    }
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: 60, color: '#8c8c8c' }}>
          <LockOutlined /> You don't have permission to view return orders.
        </div>
      </div>
    );
  }

  // Summary stats
  const draftCount      = returnOrders.filter((r) => r.status === 'draft').length;
  const approvedCount   = returnOrders.filter((r) => r.status === 'approved').length;
  const pendingCNCount  = creditNotes.filter((c) => c.status === 'received').length;

  const returnColumns = [
    {
      title: 'Return #',
      dataIndex: 'return_order_number',
      key: 'return_order_number',
      width: 160,
      render: (v) => <Text style={{ color: '#fa8c16', fontWeight: 600 }}>{v}</Text>,
    },
    {
      title: 'PO #',
      dataIndex: 'purchase_order_id',
      key: 'po_id',
      width: 70,
      render: (v) => <Text style={{ color: '#1890ff' }}>#{v}</Text>,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 160,
      ellipsis: true,
      render: (v) => <Tooltip title={v}><span>{v}</span></Tooltip>,
    },
    {
      title: 'Return Date',
      dataIndex: 'return_date',
      key: 'return_date',
      width: 110,
      render: fmtDate,
      sorter: (a, b) => new Date(a.return_date) - new Date(b.return_date),
    },
    {
      title: 'Items',
      key: 'items',
      width: 70,
      align: 'center',
      render: (_, r) => <Tag color="default">{r.items?.length || 0}</Tag>,
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (v) => <Text strong style={{ color: '#cf1322' }}>{fmtMoney(v)}</Text>,
      sorter: (a, b) => a.total_amount - b.total_amount,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: getReturnStatusTag,
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button type="primary" ghost size="small" icon={<EyeOutlined />}
              onClick={() => setDetailId(record.id)} />
          </Tooltip>
          {canUpdate && record.status === 'draft' && (
            <Popconfirm
              title="Approve this return order?"
              onConfirm={() => dispatch(approveReturnOrderRequest(record.id))}
              okText="Approve"
            >
              <Tooltip title="Approve">
                <Button type="primary" size="small" icon={<CheckCircleOutlined />} loading={actionLoading}>
                  Approve
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'returns',
      label: (
        <Space>
          <UndoOutlined />
          Return Orders
          {draftCount > 0 && <Badge count={draftCount} size="small" />}
        </Space>
      ),
      children: (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <Space wrap>
              <Select
                placeholder="Filter by status"
                style={{ width: 170 }}
                allowClear
                value={statusFilter || undefined}
                onChange={(v) => setStatus(v || '')}
              >
                <Option value="draft">🩶 Draft</Option>
                <Option value="approved">🔵 Approved</Option>
                <Option value="dispatched">🟡 Dispatched</Option>
                <Option value="completed">🟢 Completed</Option>
                <Option value="cancelled">🔴 Cancelled</Option>
              </Select>
              <Button type="primary" onClick={loadReturns} icon={<ReloadOutlined />} loading={loading}>
                Filter
              </Button>
              <Button onClick={() => { setStatus(''); dispatch(fetchReturnOrdersRequest({})); }}>Clear</Button>
            </Space>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}
                style={{ background: '#fa8c16', borderColor: '#fa8c16' }}>
                New Return Order
              </Button>
            )}
          </div>

          <Table
            columns={returnColumns}
            dataSource={returnOrders}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{ pageSize: 15, showSizeChanger: false }}
            rowClassName={(r) => r.status === 'draft' ? 'return-row-draft' : ''}
            locale={{
              emptyText: (
                <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                  ↩️ No return orders found. Create one when goods need to be returned to a vendor.
                </div>
              ),
            }}
          />
        </div>
      ),
    },
    {
      key: 'credit-notes',
      label: (
        <Space>
          <DollarOutlined />
          Credit Notes
          {pendingCNCount > 0 && <Badge count={pendingCNCount} color="orange" size="small" />}
        </Space>
      ),
      children: <CreditNoteList />,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><RollbackOutlined /></div>
            <div className="page-header-text">
              <h1>Returns &amp; Credit Notes</h1>
              <p>Manage vendor returns and track credit notes for adjustments</p>
            </div>
          </div>
        </div>

        {/* ── Summary Cards ─────────────────────────────────────────── */}
        <div className="stats-grid">
          <div className="stat-card" style={{ borderLeftColor: '#fa8c16' }}>
            <div className="stat-icon" style={{ background: '#fff7e6', color: '#fa8c16' }}><RollbackOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#fa8c16' }}>{returnOrders.length}</div>
              <div className="stat-label">Total Returns</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: draftCount > 0 ? '#fa8c16' : '#52c41a' }}>
            <div className="stat-icon" style={{ background: '#fffbe6', color: '#faad14' }}><ClockCircleOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: draftCount > 0 ? '#fa8c16' : '#52c41a' }}>{draftCount}</div>
              <div className="stat-label">Awaiting Approval</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#1890ff' }}>
            <div className="stat-icon" style={{ background: '#e6f4ff', color: '#1890ff' }}><CheckCircleOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#1890ff' }}>{approvedCount}</div>
              <div className="stat-label">Approved</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: pendingCNCount > 0 ? '#fa8c16' : '#52c41a' }}>
            <div className="stat-icon" style={{ background: '#f6ffed', color: '#52c41a' }}><FileTextOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: pendingCNCount > 0 ? '#fa8c16' : '#52c41a' }}>{pendingCNCount}</div>
              <div className="stat-label">Credit Notes Pending</div>
            </div>
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────── */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabBarExtraContent={
            activeTab === 'returns' ? (
              <Button icon={<ReloadOutlined />} onClick={loadReturns} loading={loading}>
                Refresh
              </Button>
            ) : (
              <Button icon={<ReloadOutlined />}
                onClick={() => dispatch(fetchCreditNotesRequest({}))}
                loading={loading}>
                Refresh
              </Button>
            )
          }
        />
      </div>

      {/* Modals */}
      <ReturnOrderForm
        open={formOpen}
        onClose={() => { setFormOpen(false); loadReturns(); }}
      />
      {detailId && (
        <ReturnOrderDetail
          open={!!detailId}
          onClose={() => { setDetailId(null); loadReturns(); dispatch(fetchCreditNotesRequest({})); }}
          returnOrderId={detailId}
        />
      )}
    </div>
  );
};

export default ReturnOrderList;
